import React, { useState, useEffect } from 'react';
import { X, Search, Check, UserPlus } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'react-hot-toast';

const AddTeamMemberModal = ({ isOpen, onClose, teamId, teamName, onMemberAdded, existingMemberIds = [] }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);

    useEffect(() => {
        if (isOpen) {
            fetchEmployees();
            setSelectedEmployeeIds([]);
            setSearchTerm('');
        }
    }, [isOpen]);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const response = await api.get('/employees');
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast.error('Failed to load employees');
        } finally {
            setLoading(false);
        }
    };

    const toggleEmployeeSelection = (employeeId) => {
        setSelectedEmployeeIds(prev =>
            prev.includes(employeeId)
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    const handleSubmit = async () => {
        if (selectedEmployeeIds.length === 0) {
            toast.error('Please select at least one employee');
            return;
        }

        setSubmitting(true);
        try {
            await api.post(`/teams/${teamId}/employees/bulk`, {
                employee_ids: selectedEmployeeIds
            });

            toast.success(`Successfully added ${selectedEmployeeIds.length} member(s) to ${teamName}`);
            onMemberAdded();
            onClose();
        } catch (error) {
            console.error('Error adding team members:', error);
            toast.error(error.response?.data?.error || 'Failed to add team members');
        } finally {
            setSubmitting(false);
        }
    };

    // Filter employees:
    // 1. Exclude those already in the team
    // 2. Match search term
    const filteredEmployees = employees.filter(emp => {
        const isAlreadyMember = existingMemberIds.includes(emp.id);
        const matchesSearch =
            emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.email.toLowerCase().includes(searchTerm.toLowerCase());

        return !isAlreadyMember && matchesSearch;
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Add Team Members</h2>
                        <p className="text-sm text-gray-500">Add members to <span className="font-semibold text-gray-800">{teamName}</span></p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500">
                        <X size={20} />
                    </button>
                </div>

                {/* Search */}
                <div className="p-5 pb-0">
                    <div className="relative">
                        <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                        />
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    ) : filteredEmployees.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {searchTerm ? 'No employees found matching your search.' : 'All available employees are already in this team.'}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredEmployees.map(emp => {
                                const isSelected = selectedEmployeeIds.includes(emp.id);
                                return (
                                    <div
                                        key={emp.id}
                                        onClick={() => toggleEmployeeSelection(emp.id)}
                                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border transition-all ${isSelected
                                                ? 'bg-primary/5 border-primary/50'
                                                : 'bg-white border-transparent hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isSelected ? 'bg-primary border-primary text-white' : 'border-gray-300 bg-white'
                                            }`}>
                                            {isSelected && <Check size={12} strokeWidth={3} />}
                                        </div>

                                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-sm">
                                            {emp.first_name?.[0]}{emp.last_name?.[0]}
                                        </div>

                                        <div className="flex-1">
                                            <h4 className="font-medium text-gray-900">{emp.first_name} {emp.last_name}</h4>
                                            <p className="text-xs text-gray-500">{emp.email}</p>
                                        </div>

                                        <div className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                                            {emp.position || 'No Role'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-100 bg-gray-50 flex items-center justify-between rounded-b-2xl">
                    <div className="text-sm text-gray-500">
                        {selectedEmployeeIds.length} selected
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={submitting || selectedEmployeeIds.length === 0}
                            className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium shadow-lg shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {submitting ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <UserPlus size={18} />
                            )}
                            Add Members
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddTeamMemberModal;
